import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { Companies, Projects, WebserviceResponse } from "../interfaces";

import { environment as env } from "../../../environments/environment";
import { AlertService, IAlertType } from "./alert.service";
import { ContactsStoreService } from "./contacts-store.service";
import { firstValueFrom, from, Observable, of, throwError } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import moment from "moment";
import { getTenantStorage, setTenantStorage } from "../utils/tenant-context";
import { CrmPingResponse, StatusResponse } from "../interfaces/services";

@Injectable({
  providedIn: 'root'
})
export class CrmStoreService {
  devMode: boolean;
  crmEndpoint: string = this.localDevCrmEndpoint();
  rmsEndpoint: string = this.localDevBasePath('/v1/rest', env.rest);

  /**
   * Zugangsdaten fuer den CRM-Reconnect -- bewusst nur im Speicher.
   *
   * Vorher lagen sie im sessionStorage, das Passwort im Klartext. Der
   * System-Check der Login-Seite hat sie dadurch erneut eingespielt, noch
   * bevor sich jemand angemeldet hatte: auf einem geteilten Rechner mit den
   * Daten des vorherigen Benutzers. Im Speicher sind sie nach einem
   * Seitenaufbau leer, womit dieser Fall entfaellt.
   */
  private reconnectUser: string | null = null;
  private reconnectPass: string | null = null;

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService,
      private css: ContactsStoreService,
  ) {
    this.devMode = env.stages.dev;
    this.purgeStoredCredentials();
  }

  /** Entfernt Zugangsdaten, die frueherer Stand im sessionStorage abgelegt hat. */
  private purgeStoredCredentials() {
    sessionStorage.removeItem('crm_reconnect_user');
    sessionStorage.removeItem('crm_reconnect_pass');
  }

  setReconnectCredentials(user: string, pass: string) {
    this.reconnectUser = user || null;
    this.reconnectPass = pass || null;
  }

  clearReconnectCredentials() {
    this.reconnectUser = null;
    this.reconnectPass = null;
    this.purgeStoredCredentials();
  }

  private buildBaseUrl(cfg: { apiUrl: string; protocol: string; port?: number }) {
    const port = cfg.port && cfg.port !== 80 && cfg.port !== 443 ? `:${cfg.port}` : '';
    return `${cfg.protocol}://${cfg.apiUrl}${port}`;
  }

  private localDevBasePath(proxyPath: string, cfg: { apiUrl: string; protocol: string; port?: number }) {
    if (!env.production && env.stages?.dev && this.isLocalDevHost()) {
      return proxyPath;
    }
    return this.buildBaseUrl(cfg) + proxyPath;
  }

  private localDevCrmEndpoint() {
    if (!env.production && env.stages?.dev && this.isLocalDevHost()) {
      return '/crmapi';
    }
    return env.crm.protocol + '://' + env.crm.apiUrl;
  }

  private isLocalDevHost(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const host = window.location.hostname.toLowerCase();
    return host === 'localhost' || host === '127.0.0.1';
  }

  private rmsStatus(): Observable<StatusResponse | null> {
    return this.http.get<StatusResponse>(this.rmsEndpoint + '/status').pipe(
      catchError(() => of(null))
    );
  }

  private crmProvider(): Observable<string> {
    return this.rmsStatus().pipe(
      map(status => status?.crm_provider?.trim().toLowerCase() || 'tecart')
    );
  }

  crmProviderName(): Observable<string> {
    return this.crmProvider();
  }

  private rmsCrmPing(): Observable<CrmPingResponse | null> {
    return this.http.get<CrmPingResponse>(this.rmsEndpoint + '/crm/ping').pipe(
      catchError(() => of(null))
    );
  }

  async crmSignIn(username: string, userpass: string) {
    const provider = await firstValueFrom(this.crmProvider());
    if (provider !== 'tecart') {
      if (this.devMode) console.log(`crmss - direct frontend CRM login skipped for provider ${provider}`);
      return;
    }

    const options = new HttpParams({
      fromObject: {
        method: 'crmLogin',
        response_type: 'JSON',
        username: username,
        password: userpass
      }
    });

    try {
      const res = await firstValueFrom(
        this.http.get<WebserviceResponse<any>>(this.crmEndpoint + '/logon', { params: options })
      );

      if (!res) {
        throw new Error('Unerwartete Antwort: `res` ist undefined.');
      }
      const crmToken = res.toString();
      if (crmToken) {
        setTenantStorage('crm_token', crmToken);
        if (this.devMode) console.log('crmss 59 - Token erfolgreich gespeichert');
      } else {
        if (this.devMode) console.error('crmss 61 - Kein Token in der Antwort');
        throw new Error('Kein Token in der Antwort'); // Optional: Fehler werfen, wenn kein Token vorhanden ist
      }
    } catch (err) {
      if (this.devMode) console.error('crmss 65 - Fehler beim Anmelden:', err);
      throw err; // Weitergeben des Fehlers an den aufrufenden Code
    }
  }

  getCrmToken() {
    return getTenantStorage('crm_token');
  }

  private isTecartSessionError(body: any): boolean {
    if (!body) return true;
    const str = typeof body === 'string' ? body.toLowerCase() : JSON.stringify(body).toLowerCase();
    return str.includes('invalid session') || str.includes('session expired') || str.includes('not logged in');
  }

  async refreshCrmSession(): Promise<boolean> {
    const user = this.reconnectUser;
    const pass = this.reconnectPass;
    if (!user || !pass) {
      if (this.devMode) console.warn('crmss - no credentials for reconnect');
      return false;
    }
    try {
      await this.crmSignIn(user, pass);
      if (this.devMode) console.log('crmss - session reconnected successfully');
      return true;
    } catch (err) {
      if (this.devMode) console.warn('crmss - session reconnect failed', err);
      return false;
    }
  }

  private async doTecartPing(): Promise<boolean> {
    const token = this.getCrmToken();
    if (!token) return false;

    const options = new HttpParams({
      fromObject: {
        method: 'crmPing',
        response_type: 'JSON',
        session_id: token
      }
    });

    try {
      const res = await firstValueFrom(
        this.http.get<any>(`${this.crmEndpoint}/logon`, { params: options })
      );
      return !this.isTecartSessionError(res);
    } catch {
      return false;
    }
  }

  private async doTecartPingWithReconnect(): Promise<boolean> {
    const alive = await this.doTecartPing();
    if (!alive) {
      const reconnected = await this.refreshCrmSession();
      if (reconnected) {
        return this.doTecartPing();
      }
      return false;
    }
    return true;
  }

  crmHealthCheck(): Observable<boolean> {
    return this.crmProvider().pipe(
      switchMap(provider => {
        if (provider === 'hubspot') {
          return this.rmsCrmPing().pipe(
            map(response => {
              const data = response?.data;
              return !!data && data.supported && data.reachable && data.authenticated;
            })
          );
        }

        if (provider !== 'tecart') {
          return of(false);
        }

        return from(this.doTecartPingWithReconnect());
      })
    );
  }

  crmPing(token: string): Observable<boolean> {
    return this.crmProvider().pipe(
      switchMap(provider => {
        if (provider === 'hubspot') {
          return this.rmsCrmPing().pipe(
            map(response => {
              const data = response?.data;
              return !!data && data.supported && data.reachable && data.authenticated;
            })
          );
        }

        if (provider !== 'tecart') {
          return of(true);
        }

        const options = new HttpParams({
          fromObject: {
            method: 'crmPing',
            response_type: 'JSON',
            session_id: token
          }
        });

        return this.http.get<any>(`${this.crmEndpoint}/logon`, { params: options }).pipe(
          map(res => !this.isTecartSessionError(res)),
          catchError(() => of(false))
        );
      })
    );
  }

  crmLoadContact(crmId: number | null | undefined) {
    if (!crmId) {
      this.alertDispatcher.alertConfig.message = 'Keine CRM-ID vorhanden. CRM-Kontakt kann nicht geladen werden.';
      this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
      return;
    }

    this.crmProvider().pipe(take(1)).subscribe(provider => {
      if (provider !== 'tecart') {
        if (this.devMode) console.log(`crmss - direct contact detail load skipped for provider ${provider}`);
        return;
      }

      const options = new HttpParams({
        fromObject: {
          method: 'crmgetContact',
          response_type: 'JSON',
          contact_id: crmId,
          session_id: this.getCrmToken() || '',
          person_id: 0
        }
      });

      this.http.get<WebserviceResponse<any>>(this.crmEndpoint + '/contacts',
          { params: options }).subscribe({
        next: res => {
          if (!res || !res.object || res.object.length === 0) {
            this.alertDispatcher.alertConfig.message = 'Keine CRM-kontaktdaten gefunden!';
            this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
            return;
          }

          if (this.devMode) console.log('Hier der TecArt-Datensatz für CRM-ID ' + crmId);
          if (this.devMode) console.table(res);
        },
        complete: () => {

        }
      });
    });
  }

  // Contacts (Companies)

  private buildCrmContactParams(contact: Companies, userId: number, includeId = false): HttpParams {
    let params = new HttpParams()
      .set('crmContactItem[company]', contact.company ?? '')
      .set('crmContactItem[street]', contact.street ?? '')
      .set('crmContactItem[postcode]', contact.postcode ?? '')
      .set('crmContactItem[city]', contact.city ?? '')
      .set('crmContactItem[email]', contact.email ?? '')
      .set('crmContactItem[phone]', contact.phone ?? '')
      .set('crmContactItem[userid]', String(userId));

    if (includeId && contact.crm_id) {
      params = params.set('crmContactItem[id]', String(contact.crm_id));
    }

    return params;
  }

  crmAddContact(contact: Companies, userId: number) {

    const options = new HttpParams({
      fromObject: {
        method: 'crmaddContact',
        response_type: 'JSON',
        session_id: this.getCrmToken() || '',
      }
    });

    const params = this.buildCrmContactParams(contact, userId);

    return this.http.get<WebserviceResponse<number>>(
      this.crmEndpoint + '/contacts',
      { params: options.appendAll(Object.fromEntries(params.keys().map(k => [k, params.get(k) ?? '']))) }
    ).pipe(
        map(res => {
          return res;
        })
    );
  }

  crmChangeContact(contact: Companies, userId: number) {
    if (!contact.crm_id) {
      this.alertDispatcher.alertConfig.message = 'Keine CRM-ID vorhanden. Update im CRM nicht möglich.';
      this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
      return;
    }

    const options = new HttpParams({
      fromObject: {
        method: 'crmChangeContact',
        response_type: 'JSON',
        session_id: this.getCrmToken() || '',
      }
    });

    const params = this.buildCrmContactParams(contact, userId, true);

    this.http.get<WebserviceResponse<number>>(
      this.crmEndpoint + '/contacts',
      { params: options.appendAll(Object.fromEntries(params.keys().map(k => [k, params.get(k) ?? '']))) }
    ).subscribe({
      next: res => {
        let cid = res;
        if (cid) {
          this.alertDispatcher.alertConfig.message = "Die Firma ID=" + cid + " wurde im PPS und im TecArt geändert!";
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.SUCCESS;
        }
      },
      complete: () => {
        this.css.updateCompany(contact)
      }
    });
  }

  crmDeleteContact(crmId: number | null | undefined) {
    if (!crmId) {
      this.alertDispatcher.alertConfig.message = 'Keine CRM-ID vorhanden. CRM-Kontakt kann nicht gelöscht werden.';
      this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
      return;
    }

    const options = new HttpParams({
      fromObject: {
        method: 'crmdeleteContact',
        response_type: 'JSON',
        session_id: this.getCrmToken() ?? '',
      }
    });
    this.http.get<WebserviceResponse<boolean>>(this.crmEndpoint + `/contacts?contact_id=${crmId}`,
        { params: options }).subscribe({
      next: res => {
        // let controlId = res;
        if (res) {
          this.alertDispatcher.alertConfig.message = "Die Firma ID=" + crmId + " wurde gelöscht!";
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;
        }
      },
      error: () => {
        this.alertDispatcher.alertConfig.message += "Upps! Hier ist etwas schiefgelaufen! ";
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;
      },
      complete: () => { }
    });
  }

  // Projects

  crmLoadProject(pid: number) {

    const options = new HttpParams({
      fromObject: {
        method: 'crmgetProject',
        response_type: 'JSON',
        project_id: pid,
        session_id: this.getCrmToken() || ''
      }
    });
    this.http.get<WebserviceResponse<any>>(this.crmEndpoint + '/projects',
        { params: options }).subscribe({
      next: res => {
        if (!res || !res.object || res.object.length === 0) {
          this.alertDispatcher.alertConfig.message = 'Keine Projektdaten gefunden!';
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
          return;
        }


        if (this.devMode) console.log('Hier der TecArt-Datensatz für Project ' + pid);
        if (this.devMode) console.table(res);
      },
      complete: () => {

      }
    });
  }

  crmAddProject(project: Projects, projectID: string, userId: number) {
    let startTime: number = moment(project.start_project, "DD.MM.YYYY").valueOf();
    let endTime: number = moment(project.end_project, "DD.MM.YYYY").valueOf();
    // init_project liefert die Sequenz ohne fuehrende Nullen ("606", "15") --
    // so steht sie auch in der Datenbank, das Zusammensetzen zur Projektnummer
    // ist Sache der Oberflaeche. Ohne die Polsterung hiess das TecArt-Projekt
    // 29.606 statt 29.0606 und 1841.15 statt 1841.0015.
    const parsed = parseInt(projectID, 10);
    const paddedId = Number.isFinite(parsed) ? parsed.toString().padStart(4, '0') : projectID;
    let projectNumber: string = project.cid + "." + paddedId;
    let customerProjectId: string = project.cid_project_id;

    const options = new HttpParams({
      fromObject: {
        method: 'crmaddProject',
        response_type: 'JSON',
        session_id: this.getCrmToken() || '',
      }
    });
    return this.http.get<number>(this.crmEndpoint + `/projects?crmProjectItem[name]=${projectNumber}&crmProjectItem[start]=${startTime/1000}&crmProjectItem[stop]=${endTime/1000}&crmProjectItem[shortnotes]=${project.name_project}&crmProjectItem[leader]=${userId}&crmProjectItem[user22]=${userId}&crmProjectItem[user25]=${customerProjectId}`,
        { params: options }).pipe(
        map(res => {
          return res;
        })
    );
  }

  crmChangeProject(project: Projects, pid: number) {
    let startTime: number = moment(project.start_project, "DD.MM.YYYY").valueOf();
    let endTime: number = moment(project.end_project, "DD.MM.YYYY").valueOf();
    let leaderPhm: number = project.employee_phm || 0;
    let internalId: number = pid;

    const options = new HttpParams({
      fromObject: {
        method: 'crmChangeProject',
        response_type: 'JSON',
        session_id: this.getCrmToken() || '',
        project_id: internalId,
      }
    });

    return this.http.get<WebserviceResponse<number>>(this.crmEndpoint + `/projects?crmProjectItem[start]=${startTime/1000}&crmProjectItem[stop]=${endTime/1000}&crmProjectItem[shortnotes]=${project.name_project}&crmProjectItem[leader]=${leaderPhm}&crmProjectItem[user22]=${leaderPhm}&crmProjectItem[user25]=${internalId}`,
        { params: options }).pipe(
        map(res => {
          return res;
        }),
        catchError(error => {
          // Hier fängst du den Fehler ab
          if (error.status === 400) {

            this.alertDispatcher.alertConfig.message += " TecArt-Fehler! " + error.message;
            this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
            // Spezifische Behandlung für Error 400
            if (this.devMode) console.error('Bad Request Error', error.message);
            // Du kannst hier eine angepasste Fehlermeldung zurückgeben oder den Fehler weiterwerfen
            return throwError(() => new Error('Bad Request. Incorrect parameters specified on request.'));
          } else {
            // Weiterwerfen anderer Fehler
            return throwError(() => error);
          }
        })
    );
  }


  crmDeleteProject(cid: number, pid: number) {

    const options = new HttpParams({
      fromObject: {
        method: 'crmdeleteProject',
        response_type: 'JSON',
        session_id: this.getCrmToken() || '',
        project_id: pid,
      }
    });
    this.http.get<WebserviceResponse<boolean>>(this.crmEndpoint + `/projects`,
        { params: options }).subscribe({
      next: res => {
        let pid = res;
        if (pid) {
          this.alertDispatcher.alertConfig.message += " Das Tecart-Project wurde gelöscht! ";
          this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
        }
      },
      error: () => {
        this.alertDispatcher.alertConfig.message += " Hier ist etwas schiefgelaufen! ";
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.DANGER;
      },
      complete: () => { }
    });
  }
}
